import { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Backdrop,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoIcon from "@mui/icons-material/Info";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 毛玻璃效果的卡片 - 企业化设计（无hover效果）
const GlassCard = styled(Card)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  height: "100%",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 16,
    pointerEvents: "none",
  },
  // 移除hover效果 - 企业化设计
}));

// 视频容器 - 企业化设计
const VideoContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  paddingBottom: "56.25%", // 16:9 比例
  height: 0,
  overflow: "hidden",
  borderRadius: "12px",
  backgroundColor: "rgba(0, 0, 0, 0.3)",
  "& iframe": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
    borderRadius: "12px",
  },
}));

// 标题样式 - 企业化设计
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1rem",
  fontWeight: 500,
  color: "rgba(255, 255, 255, 0.9)",
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
  letterSpacing: "0.01em",
}));

// 日期标签
const DateChip = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: "rgba(255, 255, 255, 0.85)",
  fontSize: "0.9rem",
  marginBottom: theme.spacing(1),
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
}));

// 标题提示样式 - 企业化设计
const UpdateHint = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 2.5),
  borderRadius: "8px",
  background: "rgba(0, 0, 0, 0.2)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const Worship = () => {
  const [worships, setWorships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWorship, setSelectedWorship] = useState(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);

  useEffect(() => {
    const fetchWorships = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/worship`);
        console.log("Data received:", response.data);
        if (Array.isArray(response.data)) {
          setWorships(response.data);

          // 默认选择最新的崇拜
          if (response.data.length > 0) {
            setSelectedWorship(response.data[0]);
          }
        } else {
          console.error("Expected an array but got:", typeof response.data);
          setError("获取崇拜数据失败，请稍后再试");
        }
      } catch (err) {
        console.error("获取崇拜数据失败:", err);
        setError("获取崇拜数据失败，请稍后再试");
      } finally {
        setLoading(false);
      }
    };

    fetchWorships();
  }, []);

  // 从YouTube URL中提取视频ID
  const getYoutubeVideoId = (url) => {
    if (!url) return null;

    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    return match && match[2].length === 11 ? match[2] : null;
  };

  // 获取YouTube视频缩略图
  const getYoutubeThumbnail = (url) => {
    const videoId = getYoutubeVideoId(url);
    if (!videoId) return null;
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  };

  // 处理崇拜选择
  const handleWorshipSelect = (worship) => {
    if (selectedWorship?.id === worship.id) return;

    setIsLoadingVideo(true);
    setSelectedWorship(worship);

    // 滚动到页面顶部
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // 模拟视频加载时间，实际由iframe加载完成触发
    setTimeout(() => {
      setIsLoadingVideo(false);
    }, 500);
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        pt: { xs: 1, md: 1.5 },
        pb: { xs: 6, md: 8 },
        px: { xs: 2, sm: 3, md: 4 },
        scrollMarginTop: "80px",
      }}
    >
      {/* 更新提示 - 企业化设计，减少上边距 */}
      <Box
        sx={{
          mb: { xs: 1.5, md: 2 },
          mt: 0,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <UpdateHint>
          <InfoIcon
            sx={{
              fontSize: 16,
              color: "rgba(255, 255, 255, 0.7)",
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.85)",
              fontSize: { xs: "0.85rem", md: "0.9rem" },
              fontWeight: 400,
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
            }}
          >
            每周持续更新
          </Typography>
        </UpdateHint>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <CircularProgress sx={{ color: "rgba(255, 255, 255, 0.8)" }} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            textAlign: "center",
            p: 4,
            bgcolor: "rgba(255, 255, 255, 0.1)",
            borderRadius: 2,
          }}
        >
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <Grid container spacing={4} sx={{ alignItems: "flex-start" }}>
          <Grid item xs={12} md={8}>
            <GlassCard>
              <CardContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
                {selectedWorship ? (
                  <Box>
                    {/* 标题 - 放在播放器上方 */}
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: { xs: "1.4rem", md: "1.6rem" },
                        fontWeight: 600,
                        mb: 1.5,
                        color: "rgba(255, 255, 255, 0.95)",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                        letterSpacing: "-0.01em",
                        lineHeight: 1.3,
                      }}
                    >
                      {selectedWorship.title}
                    </Typography>

                    {/* 分割线 */}
                    <Divider
                      sx={{
                        mb: 2,
                        borderColor: "rgba(255, 255, 255, 0.15)",
                      }}
                    />

                    {/* 播放器 */}
                    {selectedWorship.videoUrl ? (
                      <VideoContainer sx={{ mb: 2, position: "relative" }}>
                        {isLoadingVideo && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "rgba(0, 0, 0, 0.4)",
                              backdropFilter: "blur(4px)",
                              WebkitBackdropFilter: "blur(4px)",
                              borderRadius: "12px",
                              zIndex: 2,
                              transition: "opacity 0.3s ease",
                            }}
                          >
                            <CircularProgress
                              size={40}
                              sx={{
                                color: "rgba(255, 255, 255, 0.8)",
                              }}
                            />
                          </Box>
                        )}
                        <Box
                          sx={{
                            opacity: isLoadingVideo ? 0.3 : 1,
                            transition: "opacity 0.3s ease",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <iframe
                            src={`https://www.youtube.com/embed/${getYoutubeVideoId(
                              selectedWorship.videoUrl
                            )}`}
                            title={selectedWorship.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            onLoad={() => setIsLoadingVideo(false)}
                          ></iframe>
                        </Box>
                      </VideoContainer>
                    ) : (
                      <Box
                        sx={{
                          height: 300,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(0, 0, 0, 0.05)",
                          borderRadius: 2,
                          mb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "rgba(255, 255, 255, 0.8)",
                            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                          }}
                        >
                          暂无视频
                        </Typography>
                      </Box>
                    )}

                    {/* 日期和描述 - 紧凑排列在播放器下方 */}
                    <Box>
                      {/* 日期 */}
                      <DateChip sx={{ mb: 1.5 }}>
                        <CalendarTodayIcon fontSize="small" />
                        <Typography sx={{ fontSize: "0.9rem" }}>
                          {selectedWorship.displayDate}
                        </Typography>
                      </DateChip>

                      {/* 描述 */}
                      <Typography
                        sx={{
                          color: "rgba(255, 255, 255, 0.85)",
                          whiteSpace: "pre-line",
                          textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                          lineHeight: 1.7,
                          fontSize: { xs: "0.95rem", md: "1rem" },
                        }}
                      >
                        {selectedWorship.description}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "300px",
                    }}
                  >
                    <Typography>请选择一个崇拜视频观看</Typography>
                  </Box>
                )}
              </CardContent>
            </GlassCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <GlassCard>
              <CardContent sx={{ p: 3, position: "relative", zIndex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "0.75rem", md: "0.8rem" },
                      fontWeight: 500,
                      mb: 2.5,
                      color: "rgba(255, 255, 255, 0.6)",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      flexShrink: 0,
                    }}
                  >
                    历史崇拜
                  </Typography>

                  {worships.length === 0 ? (
                    <Typography
                      sx={{
                        textAlign: "center",
                        color: "rgba(255, 255, 255, 0.8)",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      暂无崇拜记录
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0,
                      }}
                    >
                      {worships.map((worship, index) => (
                        <Box key={worship.id}>
                          <Card
                            sx={{
                              display: "flex",
                              cursor: "pointer",
                              bgcolor:
                                selectedWorship?.id === worship.id
                                  ? "rgba(0, 0, 0, 0.35)"
                                  : "rgba(0, 0, 0, 0.25)",
                              backdropFilter: "blur(10px)",
                              WebkitBackdropFilter: "blur(10px)",
                              border:
                                selectedWorship?.id === worship.id
                                  ? "1px solid rgba(255, 255, 255, 0.2)"
                                  : "1px solid rgba(255, 255, 255, 0.1)",
                              borderRadius: "8px",
                              transition: "all 0.2s ease",
                              py: 1.25,
                              px: 1.25,
                              mb: index < worships.length - 1 ? 1 : 0,
                              height: "80px",
                              "&:hover": {
                                bgcolor: "rgba(0, 0, 0, 0.3)",
                                border: "1px solid rgba(255, 255, 255, 0.15)",
                              },
                            }}
                            onClick={() => handleWorshipSelect(worship)}
                          >
                            {/* 内容区域 - 左侧 */}
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                              }}
                            >
                              {/* 日期 - 突出显示 */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  mb: 0.75,
                                }}
                              >
                                <CalendarTodayIcon
                                  sx={{
                                    fontSize: 14,
                                    color:
                                      selectedWorship?.id === worship.id
                                        ? "rgba(255, 255, 255, 0.9)"
                                        : "rgba(255, 255, 255, 0.7)",
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    color:
                                      selectedWorship?.id === worship.id
                                        ? "rgba(255, 255, 255, 0.95)"
                                        : "rgba(255, 255, 255, 0.8)",
                                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                                    fontWeight: 500,
                                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                    letterSpacing: "0.02em",
                                  }}
                                >
                                  {worship.displayDate}
                                </Typography>
                              </Box>

                              {/* 标题 - 次要信息 */}
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    selectedWorship?.id === worship.id
                                      ? "rgba(255, 255, 255, 0.85)"
                                      : "rgba(255, 255, 255, 0.7)",
                                  fontSize: { xs: "0.8rem", md: "0.85rem" },
                                  fontWeight: 400,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  lineHeight: 1.4,
                                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                  transition: "color 0.2s ease",
                                }}
                              >
                                {worship.title}
                              </Typography>
                            </Box>

                            {/* 图片 - 右侧，小尺寸 */}
                            {worship.videoUrl ? (
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "100px",
                                  height: "56px",
                                  flexShrink: 0,
                                  borderRadius: "6px",
                                  overflow: "hidden",
                                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                                  ml: 1.25,
                                }}
                              >
                                <CardMedia
                                  component="img"
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                  image={getYoutubeThumbnail(worship.videoUrl)}
                                  alt={worship.title}
                                />
                                {/* 播放图标覆盖层 */}
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                                    backdropFilter: "blur(4px)",
                                  }}
                                >
                                  <PlayArrowIcon
                                    sx={{
                                      fontSize: 14,
                                      color: "rgba(255, 255, 255, 0.95)",
                                      ml: 0.5,
                                    }}
                                  />
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: "100px",
                                  height: "56px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: "rgba(255, 255, 255, 0.05)",
                                  borderRadius: "6px",
                                  flexShrink: 0,
                                  ml: 1.25,
                                }}
                              >
                                <PlayArrowIcon
                                  sx={{
                                    fontSize: 20,
                                    color: "rgba(255, 255, 255, 0.6)",
                                  }}
                                />
                              </Box>
                            )}
                          </Card>
                          {/* 完整长度的分割线 */}
                          {index < worships.length - 1 && (
                            <Divider
                              sx={{
                                mt: 1,
                                borderColor: "rgba(255, 255, 255, 0.08)",
                                width: "100%",
                              }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Worship;
